import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { downloadDateiOderFehler } from '../lib/download';
import './DokumenteKarte.css';

const API_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';

interface TimelineDokumentItem {
  typ: string;
  titel: string;
  zeitpunkt: string | null;
  dokument_id?: number | null;
  dateiname?: string | null;
  dokument_typ?: string | null;
  richtung?: string | null;
}

interface DokumentEintrag {
  dokument_id: number;
  dateiname: string;
  dokument_typ: string;
  richtung: string;
  zeitpunkt: string | null;
  auftrag_nr?: string;
}

interface DokumenteKarteProps {
  /** Auftrag-Kontext: zeigt Dokumente mit referenz_typ="auftrag" für diesen Auftrag. */
  auftragId?: number;
  /**
   * Partner/Kunden-Kontext: zeigt Dokumente mit referenz_typ="partner" für diesen
   * Partner UND (Union) Dokumente aller Aufträge dieses Kunden (referenz_typ="auftrag").
   */
  partnerId?: number;
  authToken: string;
}

function formatDatum(iso: string | null): string {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('de-DE', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

const TYP_LABEL: Record<string, string> = {
  EINGANGSRECHNUNG: 'Eingangsrechnung',
  AUSGANGSRECHNUNG: 'Ausgangsrechnung',
  LIEFERSCHEIN_EINGANG: 'Lieferschein (Eingang)',
  LIEFERSCHEIN_AUSGANG: 'Lieferschein (Ausgang)',
  BESTELLUNG: 'Bestellung',
  AUFTRAGSBESTAETIGUNG: 'Auftragsbestätigung',
  ANGEBOT: 'Angebot',
  ZERTIFIKAT: 'Zertifikat',
  PREISLISTE: 'Preisliste',
  MAHNUNG: 'Mahnung',
  SONSTIGES: 'Sonstiges',
};

/**
 * Wiederverwendbare Dokumente-Karte für Auftrags- und Kundenakte.
 *
 * Holt die referenz-gefilterte Dokumentenliste über den Timeline-Dokumentblock
 * (GET /api/timeline?entitaet_typ=…&entitaet_id=…, filtert dort bereits auf
 * Dokument.referenz_typ/referenz_id) statt über das ungefilterte Archiv
 * (GET /api/dokumente) – letzteres kennt keinen referenz_typ/id-Filter.
 *
 * Für einen Partner wird zusätzlich die Auftragsliste des Kunden geladen und
 * je Auftrag die Timeline abgefragt (Union: Dokumente direkt am Partner ∪
 * Dokumente zu dessen Aufträgen).
 */
const DokumenteKarte: React.FC<DokumenteKarteProps> = ({ auftragId, partnerId, authToken }) => {
  const [dokumente, setDokumente] = useState<DokumentEintrag[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const headers = authToken ? { Authorization: `Bearer ${authToken}` } : {};

  const ladeFuerEntitaet = useCallback(async (
    entitaetTyp: string, entitaetId: number, auftragNr?: string,
  ): Promise<DokumentEintrag[]> => {
    const res = await axios.get(`${API_URL}/api/timeline`, {
      params: { entitaet_typ: entitaetTyp, entitaet_id: entitaetId },
      headers,
    });
    return (res.data as TimelineDokumentItem[])
      .filter(i => i.typ === 'dokument' && i.dokument_id != null)
      .map(i => ({
        dokument_id: i.dokument_id as number,
        dateiname: i.dateiname || i.titel,
        dokument_typ: i.dokument_typ || 'SONSTIGES',
        richtung: i.richtung || 'OUTBOUND',
        zeitpunkt: i.zeitpunkt,
        auftrag_nr: auftragNr,
      }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authToken]);

  const laden = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      let ergebnis: DokumentEintrag[] = [];
      if (auftragId != null) {
        ergebnis = await ladeFuerEntitaet('auftrag', auftragId);
      } else if (partnerId != null) {
        const eigene = await ladeFuerEntitaet('partner', partnerId);
        const auftraegeRes = await axios.get(`${API_URL}/api/auftraege`, {
          params: { kunde_id: partnerId },
          headers,
        });
        const auftraege: { auftrag_id: number; auftrag_nr?: string }[] = auftraegeRes.data || [];
        const proAuftrag = await Promise.all(
          auftraege.map(a => ladeFuerEntitaet('auftrag', a.auftrag_id, a.auftrag_nr))
        );
        ergebnis = [...eigene, ...proAuftrag.flat()];
      }
      ergebnis.sort((a, b) => (b.zeitpunkt || '').localeCompare(a.zeitpunkt || ''));
      setDokumente(ergebnis);
    } catch {
      setError('Dokumente konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auftragId, partnerId, ladeFuerEntitaet]);

  useEffect(() => { laden(); }, [laden]);

  /** Hinterlegte Datei herunterladen (frueher Inline-Vorschau im Modal). */
  const herunterladen = async (d: DokumentEintrag) => {
    setError('');
    const fehler = await downloadDateiOderFehler(
      `${API_URL}/api/dokumente/${d.dokument_id}/datei`,
      d.dateiname,
      d.dateiname,
    );
    if (fehler) setError(fehler);
  };

  return (
    <div className="dk-container">
      <div className="dk-header">
        <span className="dk-title">
          Dokumente
          {dokumente.length > 0 && <span className="dk-badge">{dokumente.length}</span>}
        </span>
        <button type="button" className="dk-refresh-btn" onClick={laden} disabled={loading}>
          {loading ? '⏳' : '↻'} Aktualisieren
        </button>
      </div>

      {error && <div className="dk-error">{error}</div>}

      {loading ? (
        <div className="dk-loading">Dokumente werden geladen…</div>
      ) : dokumente.length === 0 ? (
        <div className="dk-empty">Keine Dokumente vorhanden.</div>
      ) : (
        <div className="table-container">
          <table className="dk-table">
            <thead>
              <tr>
                <th className="dk-th-richtung" title="Eingang / Ausgang"></th>
                <th>Datei</th>
                <th>Typ</th>
                {partnerId != null && <th>Auftrag</th>}
                <th>Datum</th>
              </tr>
            </thead>
            <tbody>
              {dokumente.map(d => (
                <tr
                  key={d.dokument_id}
                  className="dk-row"
                  onClick={() => herunterladen(d)}
                  title="Dokument herunterladen"
                >
                  <td className="dk-td-richtung">
                    <span
                      className={`dk-richtung-dot dk-richtung-dot--${d.richtung.toLowerCase()}`}
                      title={d.richtung === 'INBOUND' ? 'Eingang' : 'Ausgang'}
                    />
                  </td>
                  <td className="dk-td-name" title={d.dateiname}>{d.dateiname}</td>
                  <td className="dk-td-typ">{TYP_LABEL[d.dokument_typ] || d.dokument_typ}</td>
                  {partnerId != null && (
                    <td className="dk-td-auftrag">{d.auftrag_nr || '–'}</td>
                  )}
                  <td className="dk-td-datum">{formatDatum(d.zeitpunkt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default DokumenteKarte;
